import { i18n } from '@/lib'
import { useField } from 'vee-validate'
import { computed, inject, ref, type Ref } from 'vue'

interface UseToggleFieldProps {
  name: string
  trueValue?: any
  defaultChecked?: boolean
  validate?: boolean
}

export function useToggleField({
  name,
  trueValue,
  defaultChecked,
  validate = true,
}: UseToggleFieldProps) {
  const formValues = inject<Ref<Record<string, any>>>('formValues', ref({}))

  const initialValue = computed(() => formValues?.value?.[name])

  const isTrueValueBoolean = computed(() =>
    trueValue !== undefined && trueValue !== '' && trueValue !== null
      ? typeof trueValue === 'boolean'
      : true,
  )

  const { value, errorMessage } = useField<any>(
    () => name,
    (value) => {
      if (!validate)
        return true
      if (value === undefined)
        return i18n('Field is required')
      return true
    },
    {
      type: 'checkbox',
      checkedValue: isTrueValueBoolean.value ? true : trueValue,
      uncheckedValue: isTrueValueBoolean.value ? false : null,
      ...(defaultChecked !== undefined ? { initialValue: initialValue.value } : {}),
    },
  )

  const booleanValue = computed(() => {
    if (value.value == null || value.value === '')
      return false
    return Boolean(value.value)
  })

  function handleUpdate(proposedValue: boolean) {
    if (isTrueValueBoolean.value) {
      value.value = proposedValue
    }
    else {
      value.value = proposedValue ? trueValue : null
    }
  }

  return {
    value,
    errorMessage,
    booleanValue,
    handleUpdate,
    isTrueValueBoolean,
  }
}
